import { useEffect, useState } from 'react';
import { getBlob } from '../api/client';

interface Props {
  invoiceId: number;
  paymentId: number;
  imageId: number;
  caption: string | null;
  canDelete?: boolean;
  onDelete?: () => void;
}

// A check-image thumbnail. Like <PersonAvatar>, it fetches the bytes with the
// auth header (a bare <img src> can't carry the in-memory Bearer token) and
// renders an object URL. Click opens the full image in a new tab.
export function PaymentImageThumb({
  invoiceId, paymentId, imageId, caption, canDelete, onDelete,
}: Props): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await getBlob(`/api/invoices/${invoiceId}/payments/${paymentId}/images/${imageId}`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        /* transient / gone — leave the placeholder */
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [invoiceId, paymentId, imageId]);

  return (
    <span className="ip-thumb">
      {url ? (
        <button
          type="button"
          className="ip-thumb-btn"
          onClick={() => window.open(url, '_blank', 'noopener')}
          title={caption ?? 'Check image'}
        >
          <img src={url} alt={caption ?? 'Check image'} />
        </button>
      ) : (
        <span className="ip-thumb-loading" aria-hidden="true" />
      )}
      {canDelete && onDelete && (
        <button
          type="button"
          className="ip-thumb-x"
          onClick={onDelete}
          aria-label="Remove image"
          title="Remove image"
        >
          ×
        </button>
      )}
    </span>
  );
}
